import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { callsAPI } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Phone, Loader2, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

const OUTCOME_STYLES = {
  order_placed: { label: "Order Placed", className: "bg-emerald-50 text-emerald-700 border border-emerald-200 badge-pill" },
  transferred: { label: "Transferred", className: "bg-amber-50 text-amber-700 border border-amber-200 badge-pill" },
  abandoned: { label: "Abandoned", className: "bg-red-50 text-red-600 border border-red-200 badge-pill" },
};

function formatDuration(s) { return `${Math.floor(s/60)}:${(s%60).toString().padStart(2,"0")}`; }
function formatTime(iso) { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }); }

export default function CallsPage() {
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCalls = async (p = page, d = date) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 15 };
      if (d) params.date = d;
      const res = await callsAPI.list(params);
      setCalls(res.data.calls);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCalls(); }, []);

  const handleDateChange = (e) => {
    const d = e.target.value;
    setDate(d);
    setPage(1);
    fetchCalls(1, d);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchCalls(newPage, date);
  };

  return (
    <div className="space-y-5 fade-in" data-testid="calls-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Calls</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} total calls recorded</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={handleDateChange} className="w-40 h-9 text-sm bg-white" data-testid="calls-date-filter" />
          {date && (
            <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setDate(""); setPage(1); fetchCalls(1, ""); }} data-testid="calls-clear-filter">
              Clear
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-100" data-testid="calls-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : calls.length === 0 ? (
            <div className="py-20 text-center" data-testid="calls-empty-state">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm text-slate-600 font-medium">No calls recorded</p>
              <p className="text-xs text-slate-400 mt-1">Test your AI by calling your assigned number.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100 hover:bg-transparent">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pl-5">Time</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Caller</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Duration</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Outcome</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Confidence</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Order</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold text-right pr-5"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calls.map(call => {
                      const os = OUTCOME_STYLES[call.outcome] || { label: call.outcome, className: "" };
                      const conf = (call.confidence * 100).toFixed(0);
                      return (
                        <TableRow key={call.id} className="cursor-pointer table-row-hover border-slate-50" onClick={() => navigate(`/calls/${call.id}`)} data-testid={`call-row-${call.id}`}>
                          <TableCell className="text-[13px] text-slate-600 pl-5 whitespace-nowrap py-3">{formatTime(call.start_time)}</TableCell>
                          <TableCell className="text-[13px] text-slate-700 font-mono tracking-tight">{call.caller_number}</TableCell>
                          <TableCell className="text-[13px] text-slate-500 tabular-nums">{formatDuration(call.duration_seconds)}</TableCell>
                          <TableCell><Badge variant="secondary" className={os.className}>{os.label}</Badge></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="confidence-bar w-10">
                                <div className="confidence-bar-fill" style={{ width: `${conf}%`, background: conf >= 80 ? '#22c55e' : conf >= 60 ? '#f59e0b' : '#ef4444' }} />
                              </div>
                              <span className="text-[12px] text-slate-500 tabular-nums">{conf}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {call.order_id
                              ? <Badge variant="secondary" className="bg-blue-50 text-blue-600 border border-blue-200 badge-pill">Yes</Badge>
                              : <span className="text-[12px] text-slate-300">--</span>}
                          </TableCell>
                          <TableCell className="text-right pr-5">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-primary" data-testid={`view-call-detail-${call.id}`}>
                              <ArrowUpRight className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {pages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100" data-testid="calls-pagination">
                  <span className="text-[12px] text-slate-400">Page {page} of {pages}</span>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => handlePageChange(page - 1)} data-testid="calls-prev-page">
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= pages} onClick={() => handlePageChange(page + 1)} data-testid="calls-next-page">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
